import { useMemo, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { HTTPError } from 'ky';
import { api } from 'lib/request/api';
import { sectionGenerationAtom } from './atoms';
import type { CrossSectionRequest, CrossSectionResponse } from './types';
import {
  groupSectionResults,
  isFetchableRequest,
  type GroupedResults,
} from './utils';

/** Query-key prefix — one cache entry per distinct cut. */
export const CROSS_SECTION_QUERY_KEY = 'cross-section:cut';

/** What the controller sends back on a 400 or 404: one message, not a list. */
interface CrossSectionErrorBody {
  error?: string;
}

/**
 * The cache key for a cut, or an idle key when there is nothing to cut.
 *
 * Keyed on the request rather than on the response's identity hashes, because the
 * key has to exist before the request goes out; the hashes then confirm on arrival
 * that the answer still matches what is on screen. React Query hashes the key with
 * a sorted-key JSON stringify, so the arrays and objects here are stable — and
 * vertex order is preserved, which matters because reordering a polyline is a
 * different line.
 *
 * @param {CrossSectionRequest | null} request - The cut being asked for, if any.
 * @return {Array} A stable key for React Query.
 */
export const crossSectionQueryKey = (request: CrossSectionRequest | null) => {
  if (request === null) {
    return [CROSS_SECTION_QUERY_KEY, 'idle'] as const;
  }
  return [
    CROSS_SECTION_QUERY_KEY,
    request.mode,
    request.structureGridCollectionId,
    request.vertices,
    request.wellUwis,
    request.ordering,
    request.gridIds,
    request.objectTypes,
    request.stepFt,
    request.wellFilters,
    request.viewport,
    // The workspace whose un-promoted drafts the cut adds beside each bore: the same
    // line answers differently under two workspaces, so it has to key the entry too.
    request.wellWorkspaceId ?? null,
  ] as const;
};

/**
 * Prefers the backend's own wording for a refused cut — "arbitrary_line requires at
 * least two vertices" is worth showing; "Request failed with status code 400" is
 * not. Falls back to the transport error when there is no body to read.
 *
 * @param {unknown} requestError - Whatever ky rejected with.
 * @return {Promise<Error>} The error to surface.
 */
const toCrossSectionError = async (requestError: unknown): Promise<Error> => {
  if (requestError instanceof HTTPError) {
    try {
      const body =
        (await requestError.response.json()) as CrossSectionErrorBody;
      if (body.error) {
        return new Error(body.error);
      }
    } catch {
      // No JSON body — fall through to the transport error.
    }
  }
  return requestError instanceof Error
    ? requestError
    : new Error('Could not calculate the cross-section.');
};

export interface UseCrossSection {
  /** The cut on screen, or undefined when there is nothing to show. */
  data: CrossSectionResponse | undefined;
  /** The cut's objects split by kind, ready to render. */
  grouped: GroupedResults;
  /** True on a first load, when there is no previous cut to hold. */
  isLoading: boolean;
  /** True whenever a request is in flight, first load or refresh. */
  isFetching: boolean;
  /**
   * True when a previous cut is still on screen while a new one resolves — the
   * "Calculated and Refreshing" state, where the plot stays put and says it is
   * resolving rather than blanking.
   */
  refreshing: boolean;
  error: Error | null;
}

/**
 * Fetches a Cross-Section cut.
 *
 * `keepPreviousData` implements the PRD's refresh rule: editing a line that already
 * has results leaves the old profile visible while the new one loads. Display-only
 * changes never reach this hook — they read the held response — so nothing fires
 * for a fill toggle or a unit switch.
 *
 * @param {CrossSectionRequest | null} request - The cut to fetch. Anything the
 *   backend would refuse (fewer than two vertices, no wells, no collection) is
 *   treated as nothing to fetch.
 * @param {number} [requestGeneration] - Which section the settled request belongs
 *   to, from `useCrossSectionRequest`. Omitted by tests that build requests by hand.
 * @return {UseCrossSection} The cut, its grouped objects, and load state.
 */
export function useCrossSection(
  request: CrossSectionRequest | null,
  requestGeneration?: number
): UseCrossSection {
  const fetchable = isFetchableRequest(request);
  // Only a fetchable request gets its own cache entry; everything else shares the
  // idle key, so a half-drawn line can't mint entries as each vertex lands.
  const activeRequest = fetchable ? request : null;

  const { data, isFetching, isPlaceholderData, error } = useQuery<
    CrossSectionResponse,
    Error
  >({
    queryKey: crossSectionQueryKey(activeRequest),
    queryFn: async ({ signal }) => {
      // `wrap_parameters :query` on the controller, so the body nests under `query`.
      // Keys stay camelCase — rails-boost deep-underscores them at the boundary, nested
      // objects included. `api` carries a prefixUrl, so the path must not start with /.
      try {
        return await api
          .post('queries/cross_sections', {
            json: { query: activeRequest },
            signal,
          })
          .json<CrossSectionResponse>();
      } catch (requestError) {
        throw await toCrossSectionError(requestError);
      }
    },
    enabled: fetchable,
    // Everything a cut depends on is in the key, so revisiting one needn't re-hit the
    // database. A Well Workspace's drafts move under an unchanged key, so the geometry
    // sheet invalidates the cut on each write.
    staleTime: 300_000,
    placeholderData: keepPreviousData,
  });

  // Two gates. React Query serves the previous data (`placeholderData`) without
  // consulting `enabled`, so a cleared section would keep handing back the old cut as a
  // success. And the old profile is only held within the generation it was fetched under.
  const generation = useAtomValue(sectionGenerationAtom);
  const shownGenerationRef = useRef(generation);
  // The settled request lags the atoms, so right after a reset the OLD request reads as
  // current for a beat. Its own generation says which section it belongs to, and only a
  // current answer stamps the ref.
  const requestIsCurrent = (requestGeneration ?? generation) === generation;
  if (
    fetchable &&
    data !== undefined &&
    !isPlaceholderData &&
    requestIsCurrent
  ) {
    shownGenerationRef.current = generation;
  }
  const crossesReset =
    !requestIsCurrent || shownGenerationRef.current !== generation;
  const held = fetchable && !crossesReset ? data : undefined;
  // Grouped under the mode that CUT this answer, not the one selected now: the two
  // diverge for a beat after a switch, and the vertical gate is mode-specific. A mode
  // switch is a reset, so the request's mode is the held answer's mode.
  const heldMode = request?.mode ?? 'arbitrary_line';
  const grouped = useMemo(
    () => groupSectionResults(held?.results, heldMode),
    [held, heldMode]
  );

  return {
    data: held,
    grouped,
    isLoading: fetchable && isFetching && held === undefined,
    isFetching: fetchable && isFetching,
    refreshing: fetchable && isFetching && isPlaceholderData,
    error: fetchable ? (error ?? null) : null,
  };
}
